import React, { useEffect } from "react"
import {
    Box,
    Checkbox,
    FormControlLabel,
    MenuItem,
    Paper,
    TextField,
    Typography,
    Button,
} from "@material-ui/core"
import { makeStyles, useTheme } from "@material-ui/core/styles"
import useMediaQuery from "@material-ui/core/useMediaQuery"
import SaveIcon from "@material-ui/icons/Save"
import LoyaltyIcon from "@material-ui/icons/Loyalty"
import SettingsIcon from "@material-ui/icons/Settings"
import { useSnackbar } from "../../../utils/notificacoes"
import EditorHtml from "../../components/EditorHtml"
import { useCadastroPromocaoFidelidadeContext } from "../context/useCadastroPromocaoFidelidadeContext"
import { CadastroPromocaoFidelidadeActionsTypes } from "../context/cadastroPromocaoFidelidadeActions"
import type { CadastroPromocaoFidelidadeModel } from "../models/cadastroPromocaoFidelidadeModel"

const useStyles = makeStyles(theme => ({
    container: {
        paddingLeft: "90px",
        paddingRight: "10px",
        paddingTop: "100px",
        paddingBottom: "30px",
        [theme.breakpoints.down("md")]: { paddingLeft: "20px", paddingRight: "20px", paddingTop: "80px" },
        [theme.breakpoints.down("sm")]: { paddingLeft: "15px", paddingRight: "15px", paddingTop: "70px" },
    },
    content: {
        margin: "auto",
        maxWidth: "800px",
        [theme.breakpoints.down("sm")]: { maxWidth: "100%" },
    },
    configCard: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { borderRadius: 15, marginBottom: theme.spacing(2) },
    },
    cardHeader: {
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #e9ecef",
        display: "flex",
        alignItems: "center",
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    cardHeaderTitle: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.1rem" },
    },
    cardContent: {
        padding: theme.spacing(3),
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(2),
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    iconHeader: {
        color: "#4caf50",
        marginRight: theme.spacing(1),
    },
    titleText: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.5rem" },
        [theme.breakpoints.down("xs")]: { fontSize: "1.25rem" },
    },
    subtitle: {
        color: "#7f8c8d",
        marginBottom: theme.spacing(2),
    },
    salvarBtn: {
        borderRadius: 25,
        backgroundColor: "var(--cor-botao-salvar)",
        color: "white",
        boxShadow: "0 4px 15px rgba(var(--cor-botao-salvar-rgb),0.3)",
        "&:hover": { backgroundColor: "var(--cor-botao-salvar-hover)" },
        alignSelf: "flex-end",
    },
    row: {
        display: "flex",
        gap: theme.spacing(2),
        [theme.breakpoints.down("xs")]: { flexDirection: "column" },
    },
}))

function campo(
    label: string,
    campo: keyof CadastroPromocaoFidelidadeModel,
    valor: string,
    dispatch: React.Dispatch<{ type: CadastroPromocaoFidelidadeActionsTypes.ATUALIZAR_CAMPO; payload: { campo: keyof CadastroPromocaoFidelidadeModel; valor: string | boolean | number } }>,
    extra?: { type?: string; multiline?: boolean; rows?: number; placeholder?: string }
) {
    return (
        <TextField
            label={label}
            variant="outlined"
            size="small"
            fullWidth
            value={valor}
            type={extra?.type}
            multiline={extra?.multiline}
            rows={extra?.rows}
            placeholder={extra?.placeholder}
            onChange={e =>
                dispatch({
                    type: CadastroPromocaoFidelidadeActionsTypes.ATUALIZAR_CAMPO,
                    payload: { campo, valor: e.target.value },
                })
            }
        />
    )
}

function checkbox(
    label: string,
    campoKey: keyof CadastroPromocaoFidelidadeModel,
    valor: boolean,
    dispatch: React.Dispatch<{ type: CadastroPromocaoFidelidadeActionsTypes.ATUALIZAR_CAMPO; payload: { campo: keyof CadastroPromocaoFidelidadeModel; valor: string | boolean | number } }>
) {
    return (
        <FormControlLabel
            control={
                <Checkbox
                    checked={valor}
                    onChange={e =>
                        dispatch({
                            type: CadastroPromocaoFidelidadeActionsTypes.ATUALIZAR_CAMPO,
                            payload: { campo: campoKey, valor: e.target.checked },
                        })
                    }
                    style={{ color: "#4caf50" }}
                />
            }
            label={label}
        />
    )
}

export function FormPromocaoFidelidade() {
    const classes = useStyles()
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
    const { dados, dispatch, salvar, erroCarregamento } = useCadastroPromocaoFidelidadeContext()
    const { enqueueSnackbar } = useSnackbar()

    const alertStart = (msg: string, tipo: "success" | "error" | "info") => {
        enqueueSnackbar(msg, { variant: tipo })
    }

    useEffect(() => {
        if (erroCarregamento) alertStart(erroCarregamento, "error")
    }, [erroCarregamento])

    const atualizar = (campoKey: keyof CadastroPromocaoFidelidadeModel, valor: string | boolean | number) => {
        dispatch({
            type: CadastroPromocaoFidelidadeActionsTypes.ATUALIZAR_CAMPO,
            payload: { campo: campoKey, valor },
        })
    }

    const handleSalvar = async () => {
        if (!dados.premio) {
            alertStart("Descrição do prêmio é obrigatória.", "error")
            return
        }
        if (!dados.quantidadeNecessaria || isNaN(Number(dados.quantidadeNecessaria))) {
            alertStart("Quantidade de selos para completar a cartela é obrigatória.", "error")
            return
        }
        try {
            await salvar()
            alertStart("Promoção de fidelidade atualizada com sucesso!", "success")
        } catch (e) {
            alertStart((e as Error).message || "Erro ao salvar promoção.", "error")
        }
    }

    const tipoPontuacaoOpcoes = [
        { value: 0, label: "Pontuar por valor consumido no pedido" },
        { value: 1, label: "Pontuar por pedido" },
        { value: 2, label: "Não pontuar no delivery" },
        { value: 3, label: "Pontuar por produto" },
    ]

    const tipoPremioOpcoes = [
        { value: -1, label: "Resgate da cartela manualmente pelo funcionário do estabelecimento" },
        { value: 0, label: "Desconto em R$ (reais) aplicado no próximo pedido pelo aplicativo" },
        {
            value: 1,
            label: "Desconto % (percentual) em relação à média dos pedidos da cartela completa",
        },
        { value: 2, label: "Desconto % (percentual) em relação ao valor do próximo pedido" },
    ]

    return (
        <Box className={classes.container}>
            <Box className={classes.content}>
                <Typography variant={isMobile ? "h5" : "h4"} className={classes.titleText} gutterBottom>
                    Programa de Fidelidade
                </Typography>
                <Typography variant="body2" className={classes.subtitle}>
                    Configure os dados da promoção de fidelidade do seu estabelecimento.
                </Typography>

                {/* Card: Promoção de Fidelidade */}
                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <LoyaltyIcon className={classes.iconHeader} />
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Promoção de Fidelidade
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        {campo("Descrição do Prêmio *", "premio", dados.premio, dispatch, {
                            placeholder: "Ex: R$10,00 em desconto",
                        })}

                        {dados.possuiDelivery && (
                            <>
                                <TextField
                                    select
                                    label="Tipo de Pontuação de selos (pontos)"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={dados.tipoPontuacao}
                                    onChange={e => atualizar("tipoPontuacao", Number(e.target.value))}
                                >
                                    {tipoPontuacaoOpcoes.map(o => (
                                        <MenuItem key={o.value} value={o.value}>
                                            {o.label}
                                        </MenuItem>
                                    ))}
                                </TextField>

                                {dados.tipoPontuacao === 0 && (
                                    <TextField
                                        label="Valor em R$ para ganhar cada selo no delivery"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={dados.valorPorSelo}
                                        type="number"
                                        placeholder="Ex: 20"
                                        onChange={e => atualizar("valorPorSelo", e.target.value)}
                                    />
                                )}

                                <TextField
                                    select
                                    label="Tipo de Premiação"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={dados.tipoPremio}
                                    onChange={e => atualizar("tipoPremio", Number(e.target.value))}
                                >
                                    {tipoPremioOpcoes.map(o => (
                                        <MenuItem key={o.value} value={o.value}>
                                            {o.label}
                                        </MenuItem>
                                    ))}
                                </TextField>

                                {dados.tipoPremio === 0 && (
                                    <TextField
                                        label="Desconto (R$) Próximo Pedido"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={dados.valorPremio}
                                        type="number"
                                        placeholder="Ex: 20"
                                        onChange={e => atualizar("valorPremio", e.target.value)}
                                    />
                                )}

                                {dados.tipoPremio === 1 && (
                                    <TextField
                                        label="Desconto (%) Média dos Pedidos"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={dados.valorPremioPercentualMedia}
                                        type="number"
                                        placeholder="Ex: 30"
                                        onChange={e =>
                                            atualizar("valorPremioPercentualMedia", e.target.value)
                                        }
                                    />
                                )}

                                {dados.tipoPremio === 2 && (
                                    <TextField
                                        label="Desconto (%) Próximo Pedido"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={dados.valorPremioPercentual}
                                        type="number"
                                        placeholder="Ex: 40"
                                        onChange={e =>
                                            atualizar("valorPremioPercentual", e.target.value)
                                        }
                                    />
                                )}
                            </>
                        )}

                        {!dados.possuiDelivery && (
                            <TextField
                                label="Consumo Mínimo *"
                                variant="outlined"
                                size="small"
                                fullWidth
                                value={dados.consumoMinimo}
                                placeholder="Ex: 10"
                                onChange={e => atualizar("consumoMinimo", e.target.value)}
                            />
                        )}

                        <Box className={classes.row}>
                            <TextField
                                label="Quantidade de selos para completar a cartela *"
                                variant="outlined"
                                size="small"
                                fullWidth
                                value={dados.quantidadeNecessaria}
                                type="number"
                                placeholder="Ex: 10"
                                onChange={e => atualizar("quantidadeNecessaria", e.target.value)}
                            />
                            <TextField
                                label="Valor mínimo do pedido para ganhar selo (R$)"
                                variant="outlined"
                                size="small"
                                fullWidth
                                value={dados.valorConsumoMinimoParaSelo}
                                type="number"
                                onChange={e =>
                                    atualizar("valorConsumoMinimoParaSelo", e.target.value)
                                }
                            />
                        </Box>

                        <TextField
                            label="Observação"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={dados.observacao}
                            placeholder="Ex: Ganhe um selo para pedidos acima de R$10,00"
                            onChange={e => atualizar("observacao", e.target.value)}
                        />

                        {checkbox(
                            "Exibir na aba promoção do app/site e enviar na mensagem de Boas Vindas",
                            "exibirPublicamente",
                            dados.exibirPublicamente,
                            dispatch
                        )}
                        {checkbox(
                            "Bonificar cliente por indicação de amigos",
                            "bonificarIndicacaoAmigos",
                            dados.bonificarIndicacaoAmigos,
                            dispatch
                        )}
                        {checkbox(
                            "Bonificar cliente por pesquisa de satisfação",
                            "bonificarPesquisaSatisfacao",
                            dados.bonificarPesquisaSatisfacao,
                            dispatch
                        )}
                        {checkbox(
                            "Ignorar pedido mínimo no resgate",
                            "ignorarPedioMinimoNoResgate",
                            dados.ignorarPedioMinimoNoResgate,
                            dispatch
                        )}
                        {checkbox(
                            "Desativar a promoção de fidelidade para retirada no local",
                            "naoPermitirFidelidadeRetiradaLocal",
                            dados.naoPermitirFidelidadeRetiradaLocal,
                            dispatch
                        )}

                        <Button
                            variant="contained"
                            className={classes.salvarBtn}
                            startIcon={<SaveIcon />}
                            onClick={handleSalvar}
                        >
                            Salvar
                        </Button>
                    </Box>
                </Paper>

                {/* Card: Regulamento */}
                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <SettingsIcon className={classes.iconHeader} />
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Regulamento
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <Typography variant="caption" style={{ color: "#7f8c8d" }}>
                            Obs: no regulamento não recomendamos alterações.
                        </Typography>
                        <EditorHtml
                            label="Regulamento da Promoção"
                            value={dados.regulamentoTexto}
                            onChange={html => atualizar("regulamentoTexto", html)}
                            altura={260}
                            placeholder="Escreva aqui o regulamento da promoção…"
                        />
                    </Box>
                </Paper>

                {/* Card: Configurações Avançadas */}
                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <SettingsIcon className={classes.iconHeader} />
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Configurações Avançadas
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <Box className={classes.row}>
                            <TextField
                                label="Fator multiplicador de selos"
                                variant="outlined"
                                size="small"
                                fullWidth
                                value={dados.fatorMultiplicadorSelos}
                                type="number"
                                onChange={e => atualizar("fatorMultiplicadorSelos", e.target.value)}
                            />
                            <TextField
                                label="Validade da cartela (em dias) *"
                                variant="outlined"
                                size="small"
                                fullWidth
                                value={dados.diasValidadeCartela}
                                type="number"
                                onChange={e => atualizar("diasValidadeCartela", e.target.value)}
                            />
                        </Box>

                        <TextField
                            label="Renovação automática da cartela (em dias)"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={dados.diasRenovacao}
                            type="number"
                            onChange={e => atualizar("diasRenovacao", e.target.value)}
                        />

                        <TextField
                            label="Máximo de selos impressos que poderão ser registrados por dia por cliente"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={dados.maximoRegistroSelosImpressosDiarios}
                            type="number"
                            onChange={e =>
                                atualizar("maximoRegistroSelosImpressosDiarios", e.target.value)
                            }
                        />

                        <TextField
                            label="Máximo de selos virtuais que poderão ser registrados por dia por cliente"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={dados.maximoRegistroSelosVirtuaisDiarios}
                            type="number"
                            onChange={e =>
                                atualizar("maximoRegistroSelosVirtuaisDiarios", e.target.value)
                            }
                        />

                        {checkbox(
                            "Permite resgate com 5 toques sobre a cartela",
                            "permiteResgateAnonimo",
                            dados.permiteResgateAnonimo,
                            dispatch
                        )}
                        {checkbox(
                            "Validar máximo de selos diários no delivery",
                            "validarMaximoDiarioDelivery",
                            dados.validarMaximoDiarioDelivery,
                            dispatch
                        )}
                        {checkbox(
                            "Frete grátis para clientes com cartela completa",
                            "freteGratisCartelaCompleta",
                            dados.freteGratisCartelaCompleta,
                            dispatch
                        )}
                        {checkbox(
                            "Considerar saldo restante ao distribuir os selos",
                            "acumularSaldoRestante",
                            dados.acumularSaldoRestante,
                            dispatch
                        )}

                        <Button
                            variant="contained"
                            className={classes.salvarBtn}
                            startIcon={<SaveIcon />}
                            onClick={handleSalvar}
                        >
                            Salvar
                        </Button>
                    </Box>
                </Paper>
            </Box>
        </Box>
    )
}
